import React, {
  useEffect,
  useState,
} from "react";

import GuestSearchInput from "./GuestSearchInput";
import ImageCropModal from "./ImageCropModal";
import { analyzeGuestId } from "../../services/aiIdService";

const MAX_FILE_SIZE =
  5 * 1024 * 1024;

const allowedTypes = [
  "image/jpeg",
  "image/png",
  "image/webp",
];

const clean = (value) =>
  String(value ?? "")
    .replace(/\s+/g, " ")
    .trim();

const normalizeId = (value) =>
  clean(value)
    .replace(/[^a-zA-Z0-9]/g, "")
    .toUpperCase();

const canonicalGender = (value) => {
  const gender = clean(value).toLowerCase();

  if (gender === "male") return "Male";
  if (gender === "female") return "Female";
  if (gender) return "Other";

  return "";
};

const canonicalIdType = (value) => {
  const type = clean(value).toLowerCase();

  if (
    type.includes("aadhaar") ||
    type.includes("aadhar")
  ) {
    return "Aadhaar Card";
  }

  if (
    type.includes("driving") ||
    type.includes("licence") ||
    type.includes("license")
  ) {
    return "Driving Licence";
  }

  if (type.includes("passport")) {
    return "Passport";
  }

  if (
    type.includes("voter") ||
    type.includes("epic")
  ) {
    return "Voter ID";
  }

  if (type.includes("pan")) {
    return "PAN Card";
  }

  return value ? "Other" : "";
};

function Field({
  label,
  children,
}) {
  return (
    <label
      style={{
        display: "block",
      }}
    >
      <div style={labelStyle}>
        {label}
      </div>

      {children}
    </label>
  );
}

function GuestDetailsForm({
  guest,
  index,
  profiles,
  disabled,
  onChange,
  onSelectProfile,
  onRemove,
  primaryAddress,
  titleOverride = "",
  hideSearch = false,
}) {
  const update = (
    field,
    value
  ) =>
    onChange(
      index,
      field,
      value
    );

  const [
    cropState,
    setCropState,
  ] = useState(null);

  const [
    frontPreview,
    setFrontPreview,
  ] = useState("");

  const [
    backPreview,
    setBackPreview,
  ] = useState("");

  const [
    aiScanning,
    setAiScanning,
  ] = useState(false);

  const [
    aiStatus,
    setAiStatus,
  ] = useState("");

  const [
    aiWarnings,
    setAiWarnings,
  ] = useState([]);


  useEffect(() => {
    return () => {
      if (frontPreview) {
        URL.revokeObjectURL(
          frontPreview
        );
      }

      if (backPreview) {
        URL.revokeObjectURL(
          backPreview
        );
      }
    };
  }, [
    frontPreview,
    backPreview,
  ]);

  const confidenceAllowed = (
    confidence
  ) => {
    const value =
      clean(confidence)
        .toLowerCase();

    return (
      !value ||
      value === "high" ||
      value === "medium"
    );
  };

  const findMatchingProfile = (
    extracted
  ) => {
    const id = normalizeId(
      extracted?.idNumber
    );

    if (!id) {
      return null;
    }

    return (
      (profiles || []).find(
        (profile) =>
          normalizeId(
            profile?.idNumber
          ) === id
      ) || null
    );
  };

  const applyAiResult = (
    result
  ) => {
    const fields =
      result?.fields || {};

    const confidence =
      result?.confidence || {};

    const apply = (
      field,
      value,
      transform = (v) => v
    ) => {
      const cleanValue =
        clean(value);

      if (!cleanValue) {
        return false;
      }

      if (
        !confidenceAllowed(
          confidence?.[field]
        )
      ) {
        return false;
      }

      update(
        field,
        transform(cleanValue)
      );

      return true;
    };

    let filled = 0;

    if (
      apply(
        "name",
        fields.name
      )
    ) {
      filled += 1;
      update(
        "searchTerm",
        fields.name
      );
    }

    if (
      apply(
        "phoneNumber",
        fields.phoneNumber,
        (value) =>
          value
            .replace(/\D/g, "")
            .slice(0, 15)
      )
    ) {
      filled += 1;
    }

    if (
      fields.age !== "" &&
      fields.age !== null &&
      fields.age !== undefined &&
      confidenceAllowed(
        confidence?.age
      )
    ) {
      const age = Number(
        fields.age
      );

      if (
        Number.isFinite(age) &&
        age > 0 &&
        age <= 120
      ) {
        update(
          "age",
          String(
            Math.round(age)
          )
        );
        filled += 1;
      }
    }

    if (
      apply(
        "gender",
        fields.gender,
        canonicalGender
      )
    ) {
      filled += 1;
    }

    if (
      apply(
        "nationality",
        fields.nationality
      )
    ) {
      filled += 1;
    }

    if (
      apply(
        "idType",
        fields.idType,
        canonicalIdType
      )
    ) {
      filled += 1;
    }

    if (
      apply(
        "idNumber",
        fields.idNumber
      )
    ) {
      filled += 1;
    }

    if (
      !guest.sameAddressAsPrimary &&
      apply(
        "address",
        fields.address
      )
    ) {
      filled += 1;
    }

    const matchedProfile =
      findMatchingProfile(
        fields
      );

    if (
      matchedProfile &&
      !guest.profileId &&
      typeof onSelectProfile ===
        "function"
    ) {
      const useExisting =
        window.confirm(
          `Existing guest mil gaya:\n\n${
            matchedProfile.name ||
            "Guest"
          }\nID: ${
            matchedProfile.idNumber ||
            fields.idNumber ||
            "-"
          }\n\nSaved guest profile use karna hai?`
        );

      if (useExisting) {
        onSelectProfile(
          index,
          matchedProfile
        );
      }
    }

    setAiWarnings(
      Array.isArray(
        result?.warnings
      )
        ? result.warnings
        : []
    );

    setAiStatus(
      `✅ AI scan complete • ${filled} fields auto-filled${
        result?.documentQuality
          ? ` • Quality: ${result.documentQuality}`
          : ""
      }`
    );
  };

  const runAiAutoFill = async (
    overrides = {}
  ) => {
    if (
      disabled ||
      aiScanning
    ) {
      return;
    }

    const frontFile =
      overrides.frontFile ??
      guest.idFrontFile;

    const backFile =
      overrides.backFile ??
      guest.idBackFile;

    const frontUrl =
      overrides.frontUrl ??
      guest.idFrontUrl;

    const backUrl =
      overrides.backUrl ??
      guest.idBackUrl;

    if (
      !frontFile &&
      !backFile &&
      !frontUrl &&
      !backUrl
    ) {
      alert(
        "Pehle ID Front ya Back photo select karo."
      );
      return;
    }

    setAiScanning(true);
    setAiWarnings([]);
    setAiStatus(
      "🤖 AI ID read kar raha hai..."
    );

    try {
      const result =
        await analyzeGuestId({
          frontFile,
          backFile,
          frontUrl,
          backUrl,
        });

      applyAiResult(result);
    } catch (error) {
      console.error(
        "AI ID scan error:",
        error
      );

      const message =
        error?.message ||
        "AI ID scan nahi hua.";

      setAiStatus(
        `❌ ${message}`
      );

      alert(
        `AI ID scan nahi hua:\n${message}`
      );
    } finally {
      setAiScanning(false);
    }
  };

  const selectImage = (
    event,
    side
  ) => {
    const file =
      event.target.files?.[0];

    event.target.value = "";

    if (!file) return;

    if (
      !allowedTypes.includes(
        file.type
      )
    ) {
      alert(
        "Sirf JPG, PNG ya WEBP image select karo."
      );
      return;
    }

    if (
      file.size >
      MAX_FILE_SIZE
    ) {
      alert(
        "ID image maximum 5 MB honi chahiye."
      );
      return;
    }

    const objectUrl =
      URL.createObjectURL(file);

    setCropState({
      side,
      file,
      objectUrl,
    });
  };

  const saveCroppedImage =
    (
      croppedFile
    ) => {
      if (!cropState) return;

      const side =
        cropState.side;

      let nextFrontFile =
        guest.idFrontFile;

      let nextBackFile =
        guest.idBackFile;

      if (side === "front") {
        if (frontPreview) {
          URL.revokeObjectURL(
            frontPreview
          );
        }

        const preview =
          URL.createObjectURL(
            croppedFile
          );

        setFrontPreview(preview);

        update(
          "idFrontFile",
          croppedFile
        );

        nextFrontFile =
          croppedFile;
      } else {
        if (backPreview) {
          URL.revokeObjectURL(
            backPreview
          );
        }

        const preview =
          URL.createObjectURL(
            croppedFile
          );

        setBackPreview(preview);

        update(
          "idBackFile",
          croppedFile
        );

        nextBackFile =
          croppedFile;
      }

      URL.revokeObjectURL(
        cropState.objectUrl
      );

      setCropState(null);

      /*
       * FULL AI AUTOMATION:
       * Cropped ID save hote hi AI scan automatic start.
       */
      window.setTimeout(
        () => {
          runAiAutoFill({
            frontFile:
              nextFrontFile,
            backFile:
              nextBackFile,
            frontUrl:
              guest.idFrontUrl,
            backUrl:
              guest.idBackUrl,
          });
        },
        120
      );
    };

  const cancelCrop = () => {
    if (
      cropState?.objectUrl
    ) {
      URL.revokeObjectURL(
        cropState.objectUrl
      );
    }

    setCropState(null);
  };

  const frontImage =
    frontPreview ||
    guest.idFrontUrl ||
    "";

  const backImage =
    backPreview ||
    guest.idBackUrl ||
    "";

  return (
    <>
      <section style={cardStyle}>
        <div style={headerStyle}>
          <div>
            <strong>
              {titleOverride ||
                (index === 0
                  ? "Primary Guest"
                  : `Guest ${
                      index + 1
                    }`)}
            </strong>

            {guest.profileId && (
              <span
                style={
                  existingBadge
                }
              >
                Returning Guest
              </span>
            )}
          </div>

          {index > 0 && (
            <button
              type="button"
              disabled={disabled}
              onClick={() =>
                onRemove(index)
              }
              style={
                removeButton
              }
            >
              Remove
            </button>
          )}
        </div>


        <div style={quickIdBoxStyle}>
          <div style={quickIdTextStyle}>
            <strong>📸 Quick ID Capture / Upload</strong>
            <span>Phone se yahin se ID click ya upload karo. Crop save hote hi AI auto-fill start ho jayega.</span>
          </div>

          <div style={quickIdActionsStyle}>
            <label style={quickCameraButtonStyle}>
              📷 Click Front
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                capture="environment"
                disabled={disabled}
                onChange={(event) => selectImage(event, "front")}
                style={{ display: "none" }}
              />
            </label>

            <label style={quickUploadButtonStyle}>
              ⬆ Upload Front
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                disabled={disabled}
                onChange={(event) => selectImage(event, "front")}
                style={{ display: "none" }}
              />
            </label>

            <label style={quickCameraButtonStyle}>
              📷 Click Back
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                capture="environment"
                disabled={disabled}
                onChange={(event) => selectImage(event, "back")}
                style={{ display: "none" }}
              />
            </label>

            <label style={quickUploadButtonStyle}>
              ⬆ Upload Back
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                disabled={disabled}
                onChange={(event) => selectImage(event, "back")}
                style={{ display: "none" }}
              />
            </label>
          </div>
        </div>

        <div style={aiAutomationBox}>
          <div>
            <div style={aiTitleStyle}>
              🤖 AI ID Auto Fill
            </div>

            <div style={aiHelpStyle}>
              ID Front/Back select + crop karo. Crop save hote hi Name, Age, Gender, ID Type, ID Number aur Address automatically read honge. Low-confidence value auto-fill nahi hogi.
            </div>
          </div>

          <button
            type="button"
            disabled={
              disabled ||
              aiScanning ||
              (!guest.idFrontFile &&
                !guest.idBackFile &&
                !guest.idFrontUrl &&
                !guest.idBackUrl &&
                !frontPreview &&
                !backPreview)
            }
            onClick={() =>
              runAiAutoFill()
            }
            style={{
              ...aiButtonStyle,
              opacity:
                disabled ||
                aiScanning
                  ? 0.6
                  : 1,
            }}
          >
            {aiScanning
              ? "⏳ Reading ID..."
              : "✨ Scan ID with AI"}
          </button>
        </div>

        {aiStatus && (
          <div style={ocrStatus}>
            {aiStatus}
          </div>
        )}

        {aiWarnings.length > 0 && (
          <div style={warningBoxStyle}>
            <strong>
              Please verify:
            </strong>

            {aiWarnings.map(
              (warning, warningIndex) => (
                <div
                  key={`${warning}-${warningIndex}`}
                  style={{
                    marginTop: 3,
                  }}
                >
                  • {warning}
                </div>
              )
            )}
          </div>
        )}

        {!hideSearch && (
          <Field label="Search Existing Guest">
            <GuestSearchInput
              profiles={profiles}
              value={guest.searchTerm || ""}
              disabled={disabled}
              onChange={(value) => update("searchTerm", value)}
              onSelect={(profile) => onSelectProfile(index, profile)}
            />
          </Field>
        )}

        <div style={gridStyle}>
          <Field label="Full Name *">
            <input
              value={
                guest.name || ""
              }
              disabled={disabled}
              onChange={(e) =>
                update(
                  "name",
                  e.target.value
                )
              }
              style={inputStyle}
            />
          </Field>

          <Field
            label={
              index === 0
                ? "Phone Number *"
                : "Phone Number"
            }
          >
            <input
              value={
                guest.phoneNumber ||
                ""
              }
              disabled={disabled}
              inputMode="numeric"
              onChange={(e) =>
                update(
                  "phoneNumber",
                  e.target.value
                    .replace(
                      /\D/g,
                      ""
                    )
                    .slice(
                      0,
                      15
                    )
                )
              }
              style={inputStyle}
            />
          </Field>

          <Field label="Age *">
            <input
              type="number"
              min="1"
              max="120"
              value={
                guest.age || ""
              }
              disabled={disabled}
              onChange={(e) =>
                update(
                  "age",
                  e.target.value
                )
              }
              style={inputStyle}
            />
          </Field>

          <Field label="Gender">
            <select
              value={
                guest.gender ||
                ""
              }
              disabled={disabled}
              onChange={(e) =>
                update(
                  "gender",
                  e.target.value
                )
              }
              style={inputStyle}
            >
              <option value="">
                Select Gender
              </option>

              <option>
                Male
              </option>

              <option>
                Female
              </option>

              <option>
                Other
              </option>
            </select>
          </Field>

          <Field label="Nationality *">
            <input
              value={
                guest.nationality ||
                ""
              }
              disabled={disabled}
              onChange={(e) =>
                update(
                  "nationality",
                  e.target.value
                )
              }
              style={inputStyle}
            />
          </Field>

          {index > 0 && (
            <Field label="Relation">
              <input
                value={
                  guest.relation ||
                  ""
                }
                disabled={disabled}
                placeholder="Friend / Wife / Colleague"
                onChange={(e) =>
                  update(
                    "relation",
                    e.target.value
                  )
                }
                style={inputStyle}
              />
            </Field>
          )}

          <Field label="ID Type *">
            <select
              value={
                guest.idType ||
                "Aadhaar Card"
              }
              disabled={disabled}
              onChange={(e) =>
                update(
                  "idType",
                  e.target.value
                )
              }
              style={inputStyle}
            >
              <option>
                Aadhaar Card
              </option>

              <option>
                Driving Licence
              </option>

              <option>
                Passport
              </option>

              <option>
                Voter ID
              </option>

              <option>
                PAN Card
              </option>

              <option>
                Other
              </option>
            </select>
          </Field>

          <Field label="ID Number *">
            <input
              value={
                guest.idNumber ||
                ""
              }
              disabled={disabled}
              onChange={(e) =>
                update(
                  "idNumber",
                  e.target.value
                )
              }
              style={inputStyle}
            />
          </Field>
        </div>

        {index > 0 && (
          <label style={checkboxRow}>
            <input
              type="checkbox"
              disabled={disabled}
              checked={Boolean(
                guest.sameAddressAsPrimary
              )}
              onChange={(e) => {
                update(
                  "sameAddressAsPrimary",
                  e.target.checked
                );

                if (
                  e.target.checked
                ) {
                  update(
                    "address",
                    primaryAddress ||
                      ""
                  );
                }
              }}
            />

            Same address as
            Primary Guest
          </label>
        )}

        <Field label="Full Address *">
          <textarea
            value={
              guest.sameAddressAsPrimary
                ? primaryAddress
                : guest.address ||
                  ""
            }
            disabled={
              disabled ||
              guest.sameAddressAsPrimary
            }
            onChange={(e) =>
              update(
                "address",
                e.target.value
              )
            }
            style={{
              ...inputStyle,
              minHeight: 80,
              resize: "vertical",
            }}
          />
        </Field>

        <div style={idGrid}>
          <IdUpload
            title="ID Front Photo *"
            image={frontImage}
            disabled={disabled}
            onSelect={(e) =>
              selectImage(
                e,
                "front"
              )
            }
          />

          <IdUpload
            title="ID Back Photo"
            image={backImage}
            disabled={disabled}
            onSelect={(e) =>
              selectImage(
                e,
                "back"
              )
            }
          />
        </div>

      </section>

      {cropState && (
        <ImageCropModal
          imageUrl={
            cropState.objectUrl
          }
          title={
            cropState.side ===
            "front"
              ? "Crop ID Front Side"
              : "Crop ID Back Side"
          }
          originalFileName={
            cropState.file.name
          }
          onCancel={
            cancelCrop
          }
          onSave={
            saveCroppedImage
          }
        />
      )}

    </>
  );
}

function IdUpload({
  title,
  image,
  disabled,
  onSelect,
}) {
  return (
    <div style={idCard}>
      <div style={labelStyle}>
        {title}
      </div>

      {image ? (
        <img
          src={image}
          alt={title}
          style={idImage}
        />
      ) : (
        <div
          style={
            emptyImage
          }
        >
          No Image
        </div>
      )}

      <label
        style={
          uploadButton
        }
      >
        📷{" "}
        {image
          ? "Crop / Change ID"
          : "Select ID Photo"}

        <input
          type="file"
          accept="image/jpeg,image/png,image/webp"
          capture="environment"
          disabled={disabled}
          onChange={
            onSelect
          }
          style={{
            display:
              "none",
          }}
        />
      </label>
    </div>
  );
}

const cardStyle = {
  border:
    "1px solid #cbd5e1",
  borderRadius: 15,
  padding: 14,
  background: "#f8fafc",
  marginBottom: 14,
};

const headerStyle = {
  display: "flex",
  justifyContent:
    "space-between",
  alignItems: "center",
  gap: 10,
  marginBottom: 12,
};

const existingBadge = {
  marginLeft: 8,
  background: "#dcfce7",
  color: "#166534",
  borderRadius: 999,
  padding: "4px 7px",
  fontSize: 10,
};

const removeButton = {
  border: 0,
  background: "#fee2e2",
  color: "#b91c1c",
  padding: "7px 10px",
  borderRadius: 8,
  fontWeight: 800,
};

const aiAutomationBox = {
  display: "flex",
  justifyContent:
    "space-between",
  alignItems: "center",
  gap: 12,
  flexWrap: "wrap",
  marginBottom: 12,
  padding: 12,
  border:
    "1px solid #c4b5fd",
  borderRadius: 11,
  background:
    "linear-gradient(135deg,#f5f3ff,#eff6ff)",
};

const aiTitleStyle = {
  color: "#5b21b6",
  fontSize: 14,
  fontWeight: 900,
};

const aiHelpStyle = {
  maxWidth: 720,
  marginTop: 4,
  color: "#64748b",
  fontSize: 10,
  lineHeight: 1.45,
};

const aiButtonStyle = {
  border: 0,
  borderRadius: 9,
  padding: "10px 13px",
  background: "#7c3aed",
  color: "#ffffff",
  fontWeight: 900,
  cursor: "pointer",
};

const warningBoxStyle = {
  marginBottom: 11,
  padding: 9,
  border:
    "1px solid #fde68a",
  borderRadius: 8,
  background: "#fffbeb",
  color: "#92400e",
  fontSize: 10,
};

const gridStyle = {
  display: "grid",
  gridTemplateColumns:
    "repeat(auto-fit,minmax(200px,1fr))",
  gap: 11,
  marginTop: 12,
};

const labelStyle = {
  fontSize: 13,
  fontWeight: 800,
  color: "#334155",
  marginBottom: 6,
};

const inputStyle = {
  width: "100%",
  padding: "10px 11px",
  border:
    "1px solid #cbd5e1",
  borderRadius: 9,
  boxSizing: "border-box",
};

const checkboxRow = {
  display: "flex",
  gap: 8,
  alignItems: "center",
  margin: "12px 0",
  fontWeight: 700,
  fontSize: 13,
};

const idGrid = {
  display: "grid",
  gridTemplateColumns:
    "repeat(auto-fit,minmax(260px,1fr))",
  gap: 11,
  marginTop: 13,
};

const idCard = {
  background: "#fff",
  border:
    "1px solid #cbd5e1",
  borderRadius: 11,
  padding: 10,
};

const idImage = {
  width: "100%",
  height: 190,
  objectFit: "contain",
  background: "#f8fafc",
  borderRadius: 8,
  marginBottom: 8,
};

const emptyImage = {
  height: 130,
  border:
    "1px dashed #cbd5e1",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: 8,
  color: "#94a3b8",
  marginBottom: 8,
};

const uploadButton = {
  display: "block",
  background: "#2563eb",
  color: "#fff",
  padding: "9px 10px",
  textAlign: "center",
  borderRadius: 8,
  fontWeight: 800,
  cursor: "pointer",
};

const ocrStatus = {
  marginBottom: 11,
  background: "#eff6ff",
  color: "#1d4ed8",
  padding: 9,
  borderRadius: 8,
  fontSize: 12,
  fontWeight: 800,
};


const quickIdBoxStyle = {
  marginBottom: 12,
  padding: 11,
  border: "1px solid #bfdbfe",
  borderRadius: 11,
  background: "#eff6ff",
  display: "flex",
  gap: 10,
  alignItems: "center",
  justifyContent: "space-between",
  flexWrap: "wrap",
};

const quickIdTextStyle = {
  display: "flex",
  flexDirection: "column",
  gap: 3,
  fontSize: 11,
  color: "#334155",
  flex: "1 1 220px",
};

const quickIdActionsStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(2,minmax(115px,1fr))",
  gap: 7,
  flex: "1 1 260px",
};

const quickCameraButtonStyle = {
  background: "#0f766e",
  color: "#ffffff",
  padding: "9px 10px",
  borderRadius: 8,
  fontSize: 11,
  fontWeight: 900,
  textAlign: "center",
  cursor: "pointer",
};

const quickUploadButtonStyle = {
  background: "#2563eb",
  color: "#ffffff",
  padding: "9px 10px",
  borderRadius: 8,
  fontSize: 11,
  fontWeight: 900,
  textAlign: "center",
  cursor: "pointer",
};

export default GuestDetailsForm;